/**
 * UserAvatar — shared circular avatar component used across all ERP modules.
 *
 * Displays the user's initials inside a circle whose color is derived
 * deterministically from `userId`, so the same user always gets the same hue
 * across every surface (task cards, drawers, pickers, nav bar, …).
 *
 * Sizes
 * ─────
 *  xs  · 16 px  — compact history entries
 *  sm  · 20 px  — task card corner, chip miniature
 *  md  · 24 px  — pickers, watcher lists
 *  lg  · 28 px  — filter panel toggles, member picker rows
 *  xl  · 32 px  — member list rows, dropdown results
 *  2xl · 36 px  — nav bar / user menu
 */

import type { HTMLAttributes } from 'react'

// ── Color palette ─────────────────────────────────────────────────────────────

const PALETTE = [
  'bg-blue-100   text-blue-700   border-blue-300',
  'bg-green-100  text-green-700  border-green-300',
  'bg-purple-100 text-purple-700 border-purple-300',
  'bg-rose-100   text-rose-700   border-rose-300',
  'bg-teal-100   text-teal-700   border-teal-300',
  'bg-orange-100 text-orange-700 border-orange-300',
  'bg-primary-100 text-primary-700 border-primary-300',
  'bg-pink-100   text-pink-700   border-pink-300',
] as const

/** Deterministic Tailwind color classes derived from `userId`. */
export function avatarColorClass(userId: string): string {
  let h = 0
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) & 0xffff
  return PALETTE[h % PALETTE.length]
}

// ── Initials ──────────────────────────────────────────────────────────────────

/** Up to 2 uppercase initials — from name first, then email, then '?'. */
export function getAvatarInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('')
  }
  return email?.[0]?.toUpperCase() ?? '?'
}

// ── Sizes ─────────────────────────────────────────────────────────────────────

export type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const SIZE_CLS: Record<UserAvatarSize, string> = {
  xs:   'w-4  h-4  text-[9px]',
  sm:   'w-5  h-5  text-[10px]',
  md:   'w-6  h-6  text-[11px]',
  lg:   'w-7  h-7  text-xs',
  xl:   'w-8  h-8  text-xs',
  '2xl':'w-9  h-9  text-sm',
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface UserAvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Stable identifier used to derive a deterministic hue. */
  userId: string
  name?: string
  email?: string
  size?: UserAvatarSize
}

export function UserAvatar({ userId, name, email, size = 'md', className, ...rest }: UserAvatarProps) {
  const initials  = getAvatarInitials(name, email)
  const colorCls  = avatarColorClass(userId)
  const label     = name || email || userId

  return (
    <span
      {...rest}
      title={rest.title ?? label}
      aria-label={rest['aria-label'] ?? label}
      className={`${SIZE_CLS[size]} ${colorCls} rounded-full border flex items-center justify-center font-semibold shrink-0 select-none${className ? ` ${className}` : ''}`}
    >
      {initials}
    </span>
  )
}
